import { Controller } from '@hotwired/stimulus';
import { DirectUpload } from '@rails/activestorage';

export default class extends Controller {
  static targets = ['input', 'dropzone', 'fileList', 'thumbnail', 'preview', 'emptyState', 'template', 'rejectionMessage'];
  static values = {
    acceptMode: String,
    accept: String,
    maxSizeBytes: Number,
    maxTotalBytes: Number,
    maxTotalMessage: String,
    eagerUpload: Boolean,
  };

  connect() {
    if (this.hasInputTarget) {
      const mode = (this.acceptModeValue || 'all').toLowerCase()
      const provided =
        this.hasAcceptValue && this.acceptValue.trim().length > 0 ? this.acceptValue.trim() : null

      const fallbackAccept =
        mode === 'document'
          ? '.png,.jpg,.jpeg,.heic,.heif,.bmp,.gif,.tiff,.tif,.mp4,.mov,.doc,.docx,.pdf,.odt,.pages,.txt,.xlsx,.xls,.xlsm,.csv,.numbers,.ppt,.pptx,.key,.msg,.vcf'
          : mode === 'image'
            ? 'image/*'
            : null;

      this.dataTransfer = new DataTransfer()

      this.accumulatedFiles = []
      this.isProcessing = false

      Array.from(this.inputTarget.files).forEach(file => {
        this.dataTransfer.items.add(file)
      });
      if (provided || fallbackAccept) { this.inputTarget.setAttribute('accept', provided || fallbackAccept) }

      if (this.isDirectUpload && this._shouldAllowMultiple()) {
        this.preservedSignedIds = []
        this.renderedFileNames = new Set()
        this.inputTarget.addEventListener('direct-upload:end', this._onDirectUploadEnd.bind(this))
      }
    }
  }

  _onDirectUploadEnd() {
    if (!this._shouldAllowMultiple()) return

    setTimeout(() => {
      const form = this.inputTarget.closest('form')
      if (!form) return

      const inputName = this.inputTarget.getAttribute('name')
      if (!inputName) return

      const allHiddenFields = Array.from(
        form.querySelectorAll(`input[type="hidden"][name="${inputName}"]`)
      )
      const currentSignedIds = allHiddenFields.map(field => field.value).filter(v => v)

      this.preservedSignedIds.forEach(signedId => {
        if (!currentSignedIds.includes(signedId)) {
          const hiddenField = document.createElement('input')
          hiddenField.type = 'hidden'
          hiddenField.name = inputName
          hiddenField.value = signedId
          form.appendChild(hiddenField)
        }
      })

      const updatedHiddenFields = Array.from(
        form.querySelectorAll(`input[type="hidden"][name="${inputName}"]`)
      )
      this.preservedSignedIds = updatedHiddenFields.map(field => field.value).filter(v => v)
    }, 50)
  }

  get isDirectUpload() {
    return this.hasInputTarget && this.inputTarget.hasAttribute('data-direct-upload-url')
  }

  _shouldAllowMultiple() {
    return this.hasInputTarget && this.inputTarget.hasAttribute('multiple')
  }

  _show(el) {
    if (el) el.classList.remove('hidden')
  }
  _hide(el) {
    if (el) el.classList.add('hidden')
  }

  highlight(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.add('border-black-300', '!bg-black-50')
  }
  unhighlight(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove('border-black-300', '!bg-black-50')
  }

  handleDrop(event) {
    event.preventDefault()
    this.unhighlight(event)
    this._clearRejection()

    if (this.eagerUploadValue && this.isDirectUpload) {
      const dropped = Array.from(event.dataTransfer.files || [])
      const filesToProcess = this._shouldAllowMultiple() ? dropped : dropped.slice(0, 1)
      filesToProcess.forEach(file => this._eagerUpload(file))
      return
    }

    if (this.isDirectUpload) {
      const files = Array.from(event.dataTransfer.files || [])

      if (files.length && this.hasInputTarget) {
        const dataTransfer = new DataTransfer()

        if (this._shouldAllowMultiple()) {
          const existingFiles = Array.from(this.inputTarget.files)

          existingFiles.forEach(file => dataTransfer.items.add(file))
          files.forEach(file => dataTransfer.items.add(file))
        } else {
          const filesToAdd = files.slice(0, 1)
          filesToAdd.forEach(file => dataTransfer.items.add(file))
        }

        this.inputTarget.files = dataTransfer.files
        this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }
      return
    }

    const files = Array.from(event.dataTransfer.files || [])
    if (!files.length) return

    if (files) {
      const filesToProcess = this._shouldAllowMultiple() ? files : files.slice(0, 1)
      if (!this._shouldAllowMultiple()) {
        this.dataTransfer = new DataTransfer()
      }
      Array.from(filesToProcess).forEach(file => {
        this._handleFile(file)
      });
    }
  }

  handleChoose(event) {
    if (this.isProcessing) return
    this._clearRejection()

    const files = event.target.files;
    if (files) {
      const filesToProcess = this._shouldAllowMultiple() ? Array.from(files) : Array.from(files).slice(0, 1)

      if (this.eagerUploadValue && this.isDirectUpload) {
        filesToProcess.forEach(file => this._eagerUpload(file))
        this._clearInput()
      } else if (this.isDirectUpload && this._shouldAllowMultiple()) {
        if (!this.accumulatedFiles) {
          this.accumulatedFiles = []
        }

        const previousFileCount = this.accumulatedFiles.length

        filesToProcess.forEach(file => {
          if (!this._isWithinSize(file)) { this._noteRejection(file, { reason: 'size' }); return }
          if (!this._isAllowed(file)) { this._noteRejection(file); return }

          const isDuplicate = this.accumulatedFiles.some(f => f.name === file.name && f.size === file.size)
          if (isDuplicate) return

          if (this._wouldExceedTotal(file, this.accumulatedFiles)) { this._noteRejection(file, { reason: 'total' }); return }

          this.accumulatedFiles.push(file)

          if (!this.renderedFileNames.has(file.name)) {
            this._renderFileCardAndRead(file)
            this.renderedFileNames.add(file.name)
          }
        })

        const dataTransfer = new DataTransfer()
        this.accumulatedFiles.forEach(file => {
          dataTransfer.items.add(file)
        })
        this.inputTarget.files = dataTransfer.files

        if (this.accumulatedFiles.length > previousFileCount) {
          this.isProcessing = true
          setTimeout(() => {
            this.isProcessing = false
          }, 100)
        }
      } else if (!this._shouldAllowMultiple()) {
        this.dataTransfer = new DataTransfer()

        filesToProcess.forEach(file => {
          if (!this._isWithinSize(file)) { this._noteRejection(file, { reason: 'size' }); return }
          if (!this._isAllowed(file)) { this._noteRejection(file); return }

          // A single-attachment input keeps the existing attachment as a hidden signed_id field. Without
          // removing it the form submits both the replacement and the old signed_id under the same key, and
          // the old value wins, so the replacement is silently dropped.
          this._removePersistedAttachments()

          if (this.isDirectUpload) {
            this._renderFileCardAndRead(file)
          } else {
            this._handleFile(file)
          }
        })
      } else {
        filesToProcess.forEach(file => {
          this._handleFile(file)
        })
      }
    }
  }

  triggerReplace() {
    if (this.hasInputTarget) this.inputTarget.click()
  }

  _removePersistedAttachments() {
    const inputName = this.inputTarget.getAttribute('name')
    if (!inputName) return

    const form = this.inputTarget.closest('form')
    if (form) {
      form
        .querySelectorAll(`input[type="hidden"][name="${inputName}"]:not([data-eager-signed-id])`)
        .forEach(field => field.remove())
    }

    if (this.hasDropzoneTarget && this.hasFileListTarget) {
      this.dropzoneTarget.querySelectorAll('.attachment').forEach(card => {
        if (!this.fileListTarget.contains(card)) card.remove()
      })
    }
  }

  _eagerUpload(file) {
    if (!this._isWithinSize(file)) { this._noteRejection(file, { reason: 'size' }); return }
    if (!this._isAllowed(file)) { this._noteRejection(file); return }

    const card = this._renderEagerCard(file)
    const percentEl = card.querySelector('[data-role="percent"]')

    const delegate = {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (!event.lengthComputable || !percentEl) return
          percentEl.textContent = `${Math.min(100, Math.round((event.loaded / event.total) * 100))}%`
        })
      },
    }

    const upload = new DirectUpload(file, this.inputTarget.dataset.directUploadUrl, delegate)

    upload.create((error, blob) => {
      if (error) {
        card.remove()
        this._noteRejection(file, { reason: 'upload' })
        return
      }

      this._appendSignedId(blob.signed_id, card)
      if (percentEl) { percentEl.textContent = '100%'; this._hide(percentEl) }
      this._show(card.querySelector('[data-role="remove"]'))
    })
  }

  _renderEagerCard(file) {
    if (!this._shouldAllowMultiple()) this.fileListTarget.innerHTML = ''

    const element = this.templateTarget.content.cloneNode(true).querySelector('.attachment')
    element.querySelector('[data-role="name"]').textContent = file.name
    const removeButton = element.querySelector('[data-role="remove"] button')
    if (removeButton) removeButton.dataset.name = file.name
    this.fileListTarget.appendChild(element)

    return element
  }

  _appendSignedId(signedId, card) {
    const form = this.inputTarget.closest('form')
    if (!form) return

    const hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = this.inputTarget.getAttribute('name')
    hidden.value = signedId
    hidden.dataset.eagerSignedId = signedId
    form.appendChild(hidden)

    card.dataset.eagerSignedId = signedId
  }

  _clearInput() {
    if (this.hasInputTarget) this.inputTarget.files = new DataTransfer().files
  }

  // Full reset used when the wizard starts over (e.g. changing the valuation type): drop the
  // staged files, the rendered cards, and the signed-id hidden fields the direct upload appended.
  clearSelection() {
    if (this.hasInputTarget) {
      const form = this.inputTarget.closest('form')
      const inputName = this.inputTarget.getAttribute('name')
      if (form && inputName) {
        form.querySelectorAll(`input[type="hidden"][name="${inputName}"]`).forEach((field) => field.remove())
      }
      this.dataTransfer = new DataTransfer()
      this.inputTarget.files = this.dataTransfer.files
    }
    if (this.hasFileListTarget) this.fileListTarget.innerHTML = ''
    this.accumulatedFiles = []
    this.renderedFileNames?.clear()
    this.preservedSignedIds = []
    this._clearRejection()
  }

  _handleFile(file) {
    if (!this._isWithinSize(file)) { this._noteRejection(file, { reason: 'size' }); return }
    if (!this._isAllowed(file)) { this._noteRejection(file); return }

    const mode = (this.acceptModeValue || 'all').toLowerCase()

    if (mode === 'image') {
      if (!file.type.startsWith('image/')) return
      this._previewImage(file)
    } else {
      this.dataTransfer.items.add(file)
      this.inputTarget.files = this.dataTransfer.files
      this._renderFileCardAndRead(file)
    }
  }

  _noteRejection(file, opts = {}) {
    if (!this.hasRejectionMessageTarget) return
    if (!this._rejectedNames) this._rejectedNames = []
    if (!this._rejectedNames.includes(file.name)) this._rejectedNames.push(file.name)

    const list = this._rejectedNames.join(', ')
    if (opts.reason === 'size') {
      const maxMb = Math.round(this.maxSizeBytesValue / 1024 / 1024)
      this.rejectionMessageTarget.textContent = `File too large (max ${maxMb}MB): ${list}.`
    } else if (opts.reason === 'total') {
      const maxMb = Math.round(this.maxTotalBytesValue / 1_000_000)
      this.rejectionMessageTarget.textContent = this.hasMaxTotalMessageValue && this.maxTotalMessageValue.length > 0
        ? this.maxTotalMessageValue
        : `Total attachments cannot exceed ${maxMb}MB.`
    } else if (opts.reason === 'upload') {
      this.rejectionMessageTarget.textContent = `Upload failed: ${list}. Please try again.`
    } else {
      this.rejectionMessageTarget.textContent = `Unsupported file type: ${list}. Allowed: documents, images, spreadsheets, presentations, emails.`
    }
    this.rejectionMessageTarget.classList.remove('hidden')
  }

  _clearRejection() {
    this._rejectedNames = []
    if (!this.hasRejectionMessageTarget) return
    this.rejectionMessageTarget.textContent = ''
    this.rejectionMessageTarget.classList.add('hidden')
  }

  _isWithinSize(file) {
    if (!this.hasMaxSizeBytesValue || this.maxSizeBytesValue <= 0) return true
    return file.size <= this.maxSizeBytesValue
  }

  _wouldExceedTotal(file, acceptedFiles) {
    if (!this.hasMaxTotalBytesValue || this.maxTotalBytesValue <= 0) return false
    const acceptedBytes = (acceptedFiles || []).reduce((sum, f) => sum + f.size, 0)
    return acceptedBytes + file.size > this.maxTotalBytesValue
  }

  _isAllowed(file) {
    const mode = (this.acceptModeValue || 'all').toLowerCase()
    const provided =
      this.hasAcceptValue && this.acceptValue.trim().length > 0 ? this.acceptValue.trim() : null

    if (provided) {
      const parts = provided.split(',').map((s) => s.trim().toLowerCase())
      if (parts.includes('image/*') && file.type.toLowerCase().startsWith('image/')) return true

      const ext = (file.name.split('.').pop() || '').toLowerCase()
      if (parts.some((p) => p.startsWith('.') && p.slice(1) === ext)) return true

      if (parts.includes(file.type.toLowerCase())) return true

      return false
    }

    const ext = (file.name.split('.').pop() || '').toLowerCase()
    const type = file.type.toLowerCase()

    // Business-approved file types
    const ALLOWED = {
      image: {
        exts: new Set(['png', 'jpg', 'jpeg', 'heic', 'heif', 'bmp', 'gif', 'tiff', 'tif']),
        mimes: new Set(['image/png', 'image/jpeg', 'image/heic', 'image/heif', 'image/bmp', 'image/gif', 'image/tiff']),
      },
      video: {
        exts: new Set(['mp4', 'mov']),
        mimes: new Set(['video/mp4', 'video/quicktime']),
      },
      document: {
        exts: new Set(['doc', 'docx', 'pdf', 'odt', 'pages', 'txt']),
        mimes: new Set([
          'application/pdf',
          'text/plain',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.oasis.opendocument.text',
          'application/vnd.apple.pages',
        ]),
      },
      spreadsheet: {
        exts: new Set(['xlsx', 'xls', 'xlsm', 'csv', 'numbers']),
        mimes: new Set([
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
          'application/vnd.ms-excel.sheet.macroenabled.12',
          'text/csv',
          'application/vnd.apple.numbers',
        ]),
      },
      presentation: {
        exts: new Set(['ppt', 'pptx', 'key']),
        mimes: new Set([
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/vnd.apple.keynote',
        ]),
      },
      other: {
        exts: new Set(['msg', 'vcf']),
        mimes: new Set(['application/vnd.ms-outlook', 'text/vcard', 'text/x-vcard']),
      },
    }

    const matches = ({ exts, mimes }) => exts.has(ext) || mimes.has(type)
    const isImage = matches(ALLOWED.image)
    const isAny = Object.values(ALLOWED).some(matches)

    if (mode === 'image') return isImage
    if (mode === 'document' || mode === 'all') return isAny

    return false
  }

  _previewImage(file) {
    const reader = new FileReader()
    reader.onload = () => {
      if (this.hasThumbnailTarget) this.thumbnailTarget.src = reader.result
      this._hide(this.dropzoneTarget)
      this._hide(this.fileListTarget)
      this._show(this.previewTarget)
    };
    reader.readAsDataURL(file)
  }

  _renderFileCardAndRead(file) {
    const name = file.name
    const totalBytes = file.size

    if (!this._shouldAllowMultiple()) {
      this.fileListTarget.innerHTML = ''
    }

    const clone = this.templateTarget.content.cloneNode(true)
    const element = clone.querySelector('.attachment')
    const text = element.querySelector('[data-role="name"]')
    const percentEl = element.querySelector('[data-role="percent"]')
    const removeEl = element.querySelector('[data-role="remove"]')
    const removeButton = removeEl.querySelector('button')

    text.textContent = name
    removeButton.dataset.name = name

    this.fileListTarget.appendChild(element)

    const reader = new FileReader()
    reader.onprogress = (e) => {
      if (!e.lengthComputable) return
      const loaded = e.loaded
      const pct = Math.min(100, Math.round((loaded / totalBytes) * 100))
      if (percentEl) percentEl.textContent = `${pct}%`
    };
    reader.onloadend = () => {
      if (percentEl) {
        percentEl.textContent = '100%'
        this._hide(percentEl)
      }
      this._show(removeEl)
    };
    reader.readAsArrayBuffer(file)
  }

  _formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '0B'
    const k = 1024
    if (Math.abs(bytes) < k) return `${bytes}B`
    const units = ['KB', 'MB', 'GB', 'TB']
    let i = -1
    do {
      bytes /= k
      ++i
    } while (Math.abs(bytes) >= k && i < units.length - 1)
    return `${bytes.toFixed(bytes >= 100 ? 0 : bytes >= 10 ? 1 : 2)}${units[i]}`
  }

  removeAttachment(e) {
    e.preventDefault()
    const attachment = e.currentTarget.closest('.attachment')
    const fileName = attachment.querySelector('[data-role="name"]')?.textContent

    if (this._markPersistedAttachmentForDestruction(attachment)) {
      attachment.remove()
      return
    }

    if (this.eagerUploadValue) {
      const signedId = attachment.dataset.eagerSignedId
      if (signedId) {
        const form = this.inputTarget.closest('form')
        form?.querySelectorAll(`input[type="hidden"][data-eager-signed-id="${signedId}"]`)
          .forEach(field => field.remove())
      }
      attachment.remove()
      return
    }

    // Remove the file card from the UI
    attachment.remove()

    // Remove from rendered file names tracking
    if (fileName && this.renderedFileNames) {
      this.renderedFileNames.delete(fileName)
    }

    // For direct upload with multiple files, we need to remove the file from our accumulation array
    // and update the file input to reflect the change
    if (this.isDirectUpload && this._shouldAllowMultiple() && this.accumulatedFiles) {
      // Remove the file from our accumulated files array
      this.accumulatedFiles = this.accumulatedFiles.filter(f => f.name !== fileName)

      // Rebuild the file input with the remaining files
      // This ensures that when the form is submitted, the removed file won't be included
      const dataTransfer = new DataTransfer()
      this.accumulatedFiles.forEach(file => {
        dataTransfer.items.add(file)
      })
      this.inputTarget.files = dataTransfer.files
    } else if (!this.isDirectUpload && e.currentTarget.dataset.name) {
      // For non-direct-upload, use the original removal logic
      this.removeFileByName(e.currentTarget.dataset.name)
    }
  }

  _markPersistedAttachmentForDestruction(attachment) {
    const hiddenInput = attachment.querySelector('input[type="hidden"]:not([data-eager-signed-id])')
    if (!hiddenInput || !hiddenInput.value) return false

    const form = this.inputTarget.closest('form')
    if (!form) return false

    const destroyInput = document.createElement('input')
    destroyInput.type = 'hidden'
    destroyInput.name = hiddenInput.name.replace(/\]\[\]$/, '_destroy][]')
    destroyInput.value = hiddenInput.value
    form.appendChild(destroyInput)
    return true
  }

  removeFileByName(filename) {
    const dataTransfer = new DataTransfer()

    Array.from(this.dataTransfer.files).forEach(file => {
      if (file.name !== filename) dataTransfer.items.add(file)
    });

    this.dataTransfer = dataTransfer
    this.inputTarget.files = dataTransfer.files
  }
}
